import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { FC, useEffect } from 'react'
import CountUp from 'react-countup'
import useSound from 'use-sound'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import AnimatedDialog from '../../../sharedComponents/src/Dialogs/Common/AnimatedDialog'
import { FramedAvatar } from '../../../sharedComponents/src/globalHeader/components/FramedAvatar'
import { analyticsManager } from '../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { useScreenSize } from '../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import FeedbackShareActions from '../../Puzzles/components/FeedbackShareActions'
import { SHARE_TYPE } from '../../Puzzles/functions/shareApi'
import { getReward } from '../common'
import { REWARD_POPUP } from '../timing'

const animations = {
    '@keyframes reposition': {
        from: { transform: 'translateX(50%)' },
        to: { transform: 'translateX(0%)' },
    },
    '@keyframes fadeInOut': {
        '0%': {
            scale: '2.2',
            boxShadow: 'none !important',
            opacity: 0,
        },
        '50%': {
            opacity: 0.7,
            scale: '2.5',
        },
        '100%': {
            opacity: 1,
            scale: '1',
            boxShadow: '0 0 14px #FFE4B0 !important',
        },
    },
    '@keyframes fadeInOutSmall': {
        '0%': {
            display: 'none',
            scale: 0.6,
            opacity: 0,
        },
        '50%': {
            opacity: 0.7,
            scale: '1.4',
        },
        '100%': {
            opacity: 1,
            scale: '1.0',
        },
    },
    '@keyframes appear': {
        '0%': {
            opacity: '0',
            transform: 'scale(0)',
        },
        '100%': {
            opacity: '1',
            transform: 'scale(1)',
        },
    },
    '@keyframes showIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
}

export const RewardPopup: FC<{ type?: 'feedback' }> = ({ type }) => {
    const { isMobile } = useScreenSize()
    const userData = useStoreState((state) => state.userData)
    const setUserData = useStoreActions((state) => state.setUserData)
    const setResultsPopup = useStoreActions((state) => state.puzzleQuestView.setResultsPopup)
    const user = useStoreState((state) => state.userData.userData)
    const puzzleId = useStoreState((state) => state.puzzleQuestView.puzzleId)
    const cycleProgress = useStoreState((state) => state.puzzleQuestView.cycleProgress)
    const feedbackMessage = useStoreState((state) => state.puzzleQuestView.feedbackMessage)
    const feedbackGroupId = useStoreState((state) => state.puzzleQuestView.feedbackGroupId)

    const { unregisterDialog } = useDialogs()

    const [playFrame] = useSound(`/assets/sounds/default/Frame.mp3`, { volume: 0.5 })

    useEffect(() => {
        analyticsManager.dispatchEvent('feedbackPopupShown', {
            puzzleId: 'unknown',
            groupId: feedbackGroupId || 0,
            percentileRaw: 0,
            feedbackScore: 0,
            streakCount: 0,
            dynamicText: feedbackMessage,
            usedShowSolution: false,
            feature: 'puzzleQuest',
        })
        const lastDay = cycleProgress?.stepsToGo === 1 || !cycleProgress
        return () => {
            analyticsManager.dispatchEvent('feedbackPopupClosed', {
                puzzleId: 'unknown',
                feature: 'puzzleQuest',
            })
        }
    }, [])

    const reward = getReward(cycleProgress)

    const updateUserReward = (reward: number, season?: string) => {
        userData.userData &&
            setUserData({
                ...userData,
                userData: {
                    ...userData.userData,
                    reward: {
                        tier: reward,
                        season: season || '',
                    },
                },
            })
    }

    const onCloseHandler = () => {
        unregisterDialog(Dialogs.PUZZLE_QUEST_RESULT)
        reward && updateUserReward(reward, cycleProgress?.season)
        setResultsPopup(false)
    }

    const isTopTier = reward === cycleProgress?.rewardsThreshold.length

    const RewardsBlock = () => {
        return (
            <>
                <Box
                    sx={{
                        position: 'relative',
                        'div[data-attribute="particles"]': {
                            opacity: 0,
                            animation: `showIn ${REWARD_POPUP.particles.duration}ms ease forwards`,
                            animationDelay: `${REWARD_POPUP.particles.delay}ms`,
                        },
                        ...(!isTopTier && {
                            transform: 'translateX(50%)',
                            animation: `reposition ${REWARD_POPUP.reposition.duration}ms ease-in-out forwards`,
                            animationDelay: `${REWARD_POPUP.reposition.delay}ms`,
                            zIndex: '1000',
                        }),
                    }}
                >
                    <Typography variant={isMobile ? 'h6' : 'h4'} color="secondary">
                        New Reward:
                    </Typography>

                    <Box
                        data-attribute="particles"
                        position="absolute"
                        bottom="0"
                        width="96px"
                        height="96px"
                        sx={{
                            pointerEvents: 'none',
                            transform: 'scale(4)',
                        }}
                        zIndex="500"
                    >
                        <DotLottieReact
                            width={300}
                            height={300}
                            layout={{ fit: 'contain', align: [0, 0] }}
                            renderConfig={{
                                autoResize: true,
                            }}
                            autoplay
                            speed={0.5}
                            onAnimationStart={() => playFrame()}
                            src={`/assets/animations/puzzles/puzzle-quest/Particles_Burst.lottie`}
                        />
                    </Box>
                    <FramedAvatar
                        sx={{
                            position: 'relative',
                            zIndex: '1000',
                            'img[data-attribute="frame"]': {
                                opacity: 0,
                                scale: 2,
                                animation: `fadeInOut ${REWARD_POPUP.frame.duration}ms ease forwards`,
                                animationDelay: `${REWARD_POPUP.frame.delay}ms`,
                                zIndex: 100,
                            },
                            'img[data-attribute="reward"]': {
                                scale: 0.6,
                                opacity: 0,
                                animation: `fadeInOutSmall ${REWARD_POPUP.rewardIcon.duration}ms ease forwards`,
                                animationDelay: `${REWARD_POPUP.rewardIcon.delay}ms`,
                                zIndex: 100,
                            },
                        }}
                        season={cycleProgress?.season}
                        reward={reward}
                        avatar={user?.avatar_url || ''}
                    />
                </Box>
                {!isTopTier && (
                    <Box
                        sx={{
                            animation: `appear ${REWARD_POPUP.nextReward.duration}ms ease forwards`,
                            opacity: 0,
                            transform: 'scale(0)',
                            animationDelay: `${REWARD_POPUP.nextReward.delay}ms`,
                        }}
                    >
                        <Typography variant={isMobile ? 'h6' : 'h4'} color="secondary">
                            Next Reward:
                        </Typography>
                        <FramedAvatar
                            season={cycleProgress?.season}
                            reward={reward + 1}
                            avatar={user?.avatar_url || ''}
                        />
                    </Box>
                )}
            </>
        )
    }

    const EoC = () => {
        return (
            <>
                <Box
                    sx={
                        Boolean(reward)
                            ? {
                                  transform: 'translateX(50%)',
                                  animation: `reposition ${REWARD_POPUP.reposition.duration}ms ease forwards`,
                                  animationDelay: `${REWARD_POPUP.reposition.delay}ms`,
                              }
                            : {}
                    }
                >
                    <Typography variant={isMobile ? 'h6' : 'h4'} color="secondary">
                        Total Crowns:
                    </Typography>
                    <Box
                        sx={
                            !cycleProgress?.rewardsTotal
                                ? {
                                      '> *': {
                                          filter: 'grayscale(100%)',
                                      },
                                  }
                                : {}
                        }
                    >
                        <DotLottieReact autoplay src={`/assets/animations/puzzles/puzzle-quest/Single_Crown.lottie`} />
                    </Box>
                    <Typography mt={{ xs: -1, md: -3 }} variant="body1" textTransform="none">
                        x
                        <CountUp
                            delay={REWARD_POPUP.crownCountUp.delay / 1000}
                            duration={REWARD_POPUP.crownCountUp.duration / 1000}
                            end={cycleProgress?.rewardsTotal || 0}
                        />
                    </Typography>
                </Box>

                {Boolean(reward) && (
                    <Box
                        sx={{
                            animation: `appear ${REWARD_POPUP.nextReward.duration}ms ease forwards`,
                            opacity: 0,
                            animationDelay: `${REWARD_POPUP.nextReward.delay}ms`,
                        }}
                    >
                        <Typography variant={isMobile ? 'h6' : 'h4'} color="secondary">
                            Top Reward:
                        </Typography>
                        <FramedAvatar season={cycleProgress?.season} reward={reward} avatar={user?.avatar_url || ''} />
                    </Box>
                )}
            </>
        )
    }

    return (
        <AnimatedDialog
            key="puzzle-quest-result"
            name={Dialogs.PUZZLE_QUEST_RESULT}
            borderColor="secondary.main"
            paddingLess
            onClose={onCloseHandler}
            paperProps={{
                animationOrImage: {
                    src:
                        cycleProgress?.stepsToGo === 1
                            ? '/assets/animations/puzzles/puzzle-quest/Header_EventCompleted.lottie'
                            : '/assets/animations/puzzles/puzzle-quest/Header_Congrats.lottie',
                    verticalPosition: {
                        xs: '-190px',
                        md: '-220px',
                    },
                    horizontalPosition: {
                        xs: '-12%',
                        md: '75px',
                    },
                },
                closeIcon: {
                    verticalPosition: {
                        xs: '-85px',
                        md: '-45px',
                    },
                    horizontalPosition: {
                        xs: '0px',
                        md: '20px',
                    },
                },
            }}
        >
            <Stack
                gap={{ xs: 2, md: 2 }}
                width="100%"
                alignItems="center"
                sx={{
                    overflow: 'hidden',
                    pt: { xs: '30px', md: '60px' },
                    pb: { xs: '16px', md: '32px' },
                    px: { xs: '16px', md: '24px' },
                }}
            >
                {/* Content */}
                <Typography sx={{ maxWidth: '440px' }} variant={isMobile ? 'body4' : 'body3'}>
                    {feedbackMessage}
                </Typography>

                {(Boolean(reward && type !== 'feedback') || cycleProgress?.stepsToGo === 1) && (
                    <>
                        <Divider
                            sx={{
                                backgroundColor: 'text.disabled',
                                height: '1px',
                                width: '100%',
                                opacity: 0.5,
                            }}
                        />

                        <Box
                            sx={{
                                ...animations,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                                zIndex: 1000,
                                '> div': {
                                    flexGrow: 1,
                                    width: '50%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: { xs: 1, md: 2 },
                                    textTransform: 'uppercase',
                                },
                                '> div > div': {
                                    width: { xs: '64px', md: '96px' },
                                    height: { xs: '64px', md: '96px' },
                                    '> *': {
                                        borderRadius: '0 !important',
                                    },

                                    'img[data-attribute="frame"]': {
                                        boxShadow: '0 0 14px #FFE4B0',
                                    },
                                },
                            }}
                        >
                            {cycleProgress?.stepsToGo === 1 ? <EoC /> : <RewardsBlock />}
                        </Box>
                    </>
                )}

                {/* Actions */}
                <FeedbackShareActions
                    puzzleId={puzzleId}
                    shareType={SHARE_TYPE.PUZZLE_QUEST}
                    performanceGroupID={feedbackGroupId}
                    onClose={onCloseHandler}
                />
            </Stack>
        </AnimatedDialog>
    )
}
